import React, { useState, useMemo, useEffect } from 'react';
import { ArrowLeft, Download } from 'lucide-react';
import { useLang } from '../../core/lang';
import LangKeys from '../../core/locales/keys';

const CustomerReportView = ({ party, entries, onBack }) => {
  const { t } = useLang();
  const [businessProfile, setBusinessProfile] = useState(null);

  // Detect if the party is a supplier
  const isSupplier = useMemo(() =>
    party?.type?.toLowerCase() === 'supplier',
    [party]
  );

  useEffect(() => {
    const fetchBiz = async () => {
      try {
        const { getBusinessProfile } = await import('../../services/dashboardApi');
        const res = await getBusinessProfile();
        if (res?.success) setBusinessProfile(res.data);
      } catch (err) {
        console.error('Error fetching biz profile:', err);
      }
    };
    fetchBiz();
  }, []);

  const processedData = useMemo(() => {
    if (!entries || !Array.isArray(entries)) {
      return { rows: [], summary: {} };
    }

    const sorted = [...entries].sort((a, b) =>
      new Date(a.entry_date || a.created_at) - new Date(b.entry_date || b.created_at)
    );

    let runningBal = 0;
    const entriesWithBal = sorted.map(entry => {
      const amount = parseFloat(entry.amount) || 0;
      if (entry.entry_type === 'get') runningBal += amount;
      else runningBal -= amount;
      return { ...entry, calculated_bal: runningBal };
    });

    const grouped = [];
    let lastMonth = '';
    let totalGet = 0;
    let totalGive = 0;

    const displayEntries = [...entriesWithBal].reverse();

    displayEntries.forEach(entry => {
      const date = new Date(entry.entry_date || entry.created_at);
      const monthStr = date.toLocaleString('en-IN', {
        month: 'long',
        year: 'numeric'
      });

      if (monthStr !== lastMonth) {
        grouped.push({ type: 'month_header', label: monthStr });
        lastMonth = monthStr;
      }

      grouped.push({ type: 'entry', ...entry });

      if (entry.entry_type === 'get') {
        totalGet += parseFloat(entry.amount) || 0;
      } else {
        totalGive += parseFloat(entry.amount) || 0;
      }
    });

    return {
      rows: grouped,
      summary: {
        totalGet,
        totalGive,
        closingBalance: runningBal
      }
    };
  }, [entries]);

  const handleDownload = () => {
    window.print();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr.replace(' ', 'T'));
      if (isNaN(date.getTime())) return dateStr;
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch (e) {
      return dateStr;
    }
  };

  return (
    <div className="print-main-wrapper" style={styles.container}>
      {/* Header */}
      <div style={styles.header} className="no-print">
        <div style={styles.headerLeft}>
          <button style={styles.iconBtn} onClick={onBack}>
            <ArrowLeft size={20} />
          </button>
          <div>
            <h3 style={styles.headerTitle}>{t(LangKeys.report)}</h3>
            <span style={styles.headerSub}>{party.name}</span>
          </div>
        </div>

        <div style={styles.headerRight}>
          <button style={styles.downloadBtn} onClick={handleDownload}>
            <Download size={18} />
            <span>{t(LangKeys.print_download)}</span>
          </button>
        </div>
      </div>

      {/* Report Content */}
      <div className="no-print" style={styles.scrollArea}>
        <div id="printable-report" style={styles.reportSheet}>
          {/* Business Header */}
          <div style={styles.topHeader}>
            <img
              src={businessProfile?.logo_url || "https://khata.moxpal.com/assets/logo.png"}
              style={styles.sideLogo}
              alt="Logo"
            />

            <div style={styles.bizCenter}>
              <img
                src="https://khata.moxpal.com/assets/logo.png"
                style={{ height: '18px', marginBottom: '4px' }}
                alt="Moxpal"
              />
              <h2 style={styles.bizName}>
                {businessProfile?.business_name || t(LangKeys.my_business)}
              </h2>
              {businessProfile?.owner_name && (
                <span style={styles.subText}>
                  {t(LangKeys.owner_name)}: {businessProfile.owner_name}
                </span>
              )}
              <span style={styles.subText}>
                📞 {businessProfile?.mobile}
                {businessProfile?.gstin && ` | GST: ${businessProfile.gstin}`}
              </span>
            </div>

            <img
              src="https://khata.moxpal.com/assets/ganesh-ji.png"
              style={styles.sideLogo}
              alt="Ganesh Ji"
            />
          </div>

          {/* Statement Banner */}
          <div className="statement-banner" style={styles.statementBanner}>
            {isSupplier
              ? t(LangKeys.supplier_ledger_statement)
              : t(LangKeys.customer_ledger_statement)}
          </div>

          {/* Customer Info */}
          <div style={styles.customerInfo}>
            <h4 style={styles.custName}>
              {isSupplier ? t(LangKeys.supplier_name) : t(LangKeys.customer_name)} – {party.name}
            </h4>
            <span style={styles.custMobile}>
              {t(LangKeys.phone)}: {party.mobile}
            </span>
          </div>

          {/* Summary Cards */}
          <div className="summary-grid" style={styles.summaryGrid}>
            <div className="sum-card" style={styles.sumCard}>
              <span className="sum-label" style={styles.sumLabel}>
                {t(LangKeys.opening_balance)}
              </span>
              <span className="sum-val" style={{ ...styles.sumVal, color: '#475569' }}>
                ₹ 0
              </span>
            </div>

            <div className="sum-card" style={styles.sumCard}>
              <span className="sum-label" style={styles.sumLabel}>
                {t(LangKeys.total_give)} (-)
              </span>
              <span className="sum-val" style={{ ...styles.sumVal, color: '#DC2626' }}>
                ₹ {processedData.summary.totalGive?.toLocaleString() || 0}
              </span>
            </div>

            <div className="sum-card" style={styles.sumCard}>
              <span className="sum-label" style={styles.sumLabel}>
                {t(LangKeys.total_get)} (+)
              </span>
              <span className="sum-val" style={{ ...styles.sumVal, color: '#16A34A' }}>
                ₹ {processedData.summary.totalGet?.toLocaleString() || 0}
              </span>
            </div>

            <div className="sum-card" style={styles.sumCard}>
              <span className="sum-label" style={styles.sumLabel}>
                {t(LangKeys.closing_balance)}
              </span>
              <span className="sum-val" style={{
                ...styles.sumVal,
                color: (processedData.summary.closingBalance || 0) >= 0 ? '#16A34A' : '#DC2626'
              }}>
                ₹ {Math.abs(processedData.summary.closingBalance || 0).toLocaleString()}
                {(processedData.summary.closingBalance || 0) >= 0 ? 'Cr' : 'Dr'}
              </span>
            </div>
          </div>

          {/* Ledger Table */}
          <div style={styles.tableWrapper}>
            <table style={{ ...styles.table, tableLayout: 'fixed' }}>
              <thead>
                <tr style={styles.theadRow}>
                  <th style={{ ...styles.th, width: '12%' }}>{t(LangKeys.date).toUpperCase()}</th>
                  <th style={{ ...styles.th, width: '13%', textAlign: 'center' }}>
                    {t(LangKeys.give).toUpperCase()} (-)
                  </th>
                  <th style={{ ...styles.th, width: '13%', textAlign: 'center' }}>
                    {t(LangKeys.get).toUpperCase()} (+)
                  </th>
                  <th style={{ ...styles.th, width: '15%', textAlign: 'center' }}>
                    {t(LangKeys.balance).toUpperCase()}
                  </th>
                  <th style={{ ...styles.th, width: '22%' }}>
                    {t(LangKeys.particulars).toUpperCase()}
                  </th>
                  <th style={{ ...styles.th, width: '25%' }}>
                    {t(LangKeys.note).toUpperCase()}
                  </th>
                </tr>
              </thead>
              <tbody>
                {processedData.rows.map((row, idx) => {
                  if (row.type === 'month_header') {
                    return (
                      <tr key={idx}>
                        <td colSpan="6" className="month-row" style={styles.monthRow}>
                          {row.label}
                        </td>
                      </tr>
                    );
                  }

                  const isGet = row.entry_type === 'get';
                  return (
                    <tr key={idx} style={styles.tr}>
                      <td style={{ ...styles.td, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{formatDate(row.entry_date)}</td>
                      <td style={{
                        ...styles.td,
                        textAlign: 'center',
                        color: '#DC2626',
                        fontWeight: 'bold',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {!isGet ? `₹${parseFloat(row.amount).toLocaleString()}` : ''}
                      </td>
                      <td style={{
                        ...styles.td,
                        textAlign: 'center',
                        color: '#16A34A',
                        fontWeight: 'bold',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {isGet ? `₹${parseFloat(row.amount).toLocaleString()}` : ''}
                      </td>
                      <td style={{
                        ...styles.td,
                        textAlign: 'center',
                        fontWeight: 'bold',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        ₹{Math.abs(row.calculated_bal).toLocaleString()}
                        {row.calculated_bal >= 0 ? 'Cr' : 'Dr'}
                      </td>
                      <td style={{ ...styles.td, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <span style={{ fontWeight: 'bold', fontSize: '12px' }}>
                          {row.party_name || (isGet ? t(LangKeys.received) : t(LangKeys.paid))}
                        </span>
                      </td>
                      <td style={{ ...styles.td, wordBreak: 'break-word' }}>
                        {row.note || '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Footer */}
          <div style={styles.footerPrint}>
            <p>
              {new Date().toLocaleDateString('en-IN', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })} • {t(LangKeys.generated_by)} Moxpal Khata
            </p>
          </div>
        </div>
      </div>

      {/* Printable Report Sheet */}
      <div className="print-only" id="customer-report-pdf-template" style={{
        padding: '10mm 15mm',
        boxSizing: 'border-box',
        background: 'white',
        fontFamily: "'Outfit', 'Hind', sans-serif",
        color: '#1e293b',
        textAlign: 'left',
        width: '100%'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
          <div style={{ width: '60px', height: '60px', display: 'flex', alignItems: 'center' }}>
            {businessProfile?.logo_url && <img src={businessProfile.logo_url} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} alt="" />}
          </div>
          <div style={{ textAlign: 'center', flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <img src="https://khata.moxpal.com/assets/logo.png" style={{ height: '22px', marginBottom: '6px' }} alt="Moxpal" />
            <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#0f172a', margin: 0 }}>{businessProfile?.business_name || t(LangKeys.my_business)}</h2>
            {businessProfile?.owner_name && <div style={{ fontSize: '12px', color: '#475569', marginTop: '4px' }}>{t(LangKeys.name)}: {businessProfile.owner_name}</div>}
            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>{t(LangKeys.mobile_no)}: {businessProfile?.mobile || ''}</div>
          </div>
          <div style={{ width: '60px', height: '60px', display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start' }}>
            <img src="https://khata.moxpal.com/assets/ganesh-ji.png" style={{ width: '40px', height: '40px', objectFit: 'contain' }} alt="Ganesh" />
          </div>
        </div>

        <div style={{ background: '#1e40af', color: 'white', padding: '10px', textAlign: 'center', fontWeight: 'bold', fontSize: '16px', margin: '20px 0', borderRadius: '4px', textTransform: 'uppercase' }}>
          {isSupplier ? t(LangKeys.supplier_ledger_statement) : t(LangKeys.customer_ledger_statement)}
        </div>

        <div style={{ textAlign: 'center', fontSize: '14px', fontWeight: 'bold', color: '#1E293B', marginBottom: '20px' }}>
          {isSupplier ? t(LangKeys.supplier_name) : t(LangKeys.customer_name)} – {party.name}
          <div style={{ fontSize: '12px', color: '#64748B', fontWeight: '600', marginTop: '4px' }}>{t(LangKeys.phone)}: {party.mobile}</div>
        </div>

        <div style={{ display: 'flex', gap: '15px', marginBottom: '30px' }}>
          <div style={{ flex: 1, border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px', textAlign: 'center', backgroundColor: '#f8fafc' }}>
            <span style={{ fontSize: '10px', color: '#64748b', textTransform: 'uppercase', fontWeight: '700', marginBottom: '4px', display: 'block' }}>{t(LangKeys.opening_balance)}</span>
            <span style={{ fontSize: '15px', fontWeight: '800', color: '#475569' }}>₹ 0</span>
          </div>
          <div style={{ flex: 1, border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px', textAlign: 'center', backgroundColor: '#f8fafc' }}>
            <span style={{ fontSize: '10px', color: '#64748b', textTransform: 'uppercase', fontWeight: '700', marginBottom: '4px', display: 'block' }}>{t(LangKeys.total_give)} (-)</span>
            <span style={{ fontSize: '15px', fontWeight: '800', color: '#DC2626' }}>₹ {processedData.summary.totalGive?.toLocaleString() || 0}</span>
          </div>
          <div style={{ flex: 1, border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px', textAlign: 'center', backgroundColor: '#f8fafc' }}>
            <span style={{ fontSize: '10px', color: '#64748b', textTransform: 'uppercase', fontWeight: '700', marginBottom: '4px', display: 'block' }}>{t(LangKeys.total_get)} (+)</span>
            <span style={{ fontSize: '15px', fontWeight: '800', color: '#16A34A' }}>₹ {processedData.summary.totalGet?.toLocaleString() || 0}</span>
          </div>
          <div style={{ flex: 1, border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px', textAlign: 'center', backgroundColor: '#f8fafc' }}>
            <span style={{ fontSize: '10px', color: '#64748b', textTransform: 'uppercase', fontWeight: '700', marginBottom: '4px', display: 'block' }}>{t(LangKeys.closing_balance)}</span>
            <span style={{ fontSize: '15px', fontWeight: '800', color: (processedData.summary.closingBalance || 0) >= 0 ? '#16A34A' : '#DC2626' }}>
              ₹ {Math.abs(processedData.summary.closingBalance || 0).toLocaleString()}
              {(processedData.summary.closingBalance || 0) >= 0 ? 'Cr' : 'Dr'}
            </span>
          </div>
        </div>

        <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#0f172a', marginBottom: '12px', borderLeft: '4px solid #1e40af', paddingLeft: '10px' }}>
          Ledger Transactions
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #cbd5e1', tableLayout: 'fixed' }}>
          <thead>
            <tr>
              <th style={{ border: '1px solid #cbd5e1', padding: '10px', background: '#f8fafc', fontSize: '11px', fontWeight: '800', color: '#475569', textAlign: 'left', width: '15%' }}>{t(LangKeys.date).toUpperCase()}</th>
              <th style={{ border: '1px solid #cbd5e1', padding: '10px', background: '#f8fafc', fontSize: '11px', fontWeight: '800', color: '#475569', textAlign: 'center', width: '15%' }}>{t(LangKeys.give).toUpperCase()} (-)</th>
              <th style={{ border: '1px solid #cbd5e1', padding: '10px', background: '#f8fafc', fontSize: '11px', fontWeight: '800', color: '#475569', textAlign: 'center', width: '15%' }}>{t(LangKeys.get).toUpperCase()} (+)</th>
              <th style={{ border: '1px solid #cbd5e1', padding: '10px', background: '#f8fafc', fontSize: '11px', fontWeight: '800', color: '#475569', textAlign: 'center', width: '15%' }}>{t(LangKeys.balance).toUpperCase()}</th>
              <th style={{ border: '1px solid #cbd5e1', padding: '10px', background: '#f8fafc', fontSize: '11px', fontWeight: '800', color: '#475569', textAlign: 'left', width: '20%' }}>{t(LangKeys.particulars).toUpperCase()}</th>
              <th style={{ border: '1px solid #cbd5e1', padding: '10px', background: '#f8fafc', fontSize: '11px', fontWeight: '800', color: '#475569', textAlign: 'left', width: '20%' }}>{t(LangKeys.note).toUpperCase()}</th>
            </tr>
          </thead>
          <tbody>
            {processedData.rows.map((row, idx) => {
              if (row.type === 'month_header') {
                return (
                  <tr key={idx}>
                    <td colSpan="6" style={{ background: '#E0E7FF', color: '#312E81', padding: '8px 12px', fontWeight: '800', fontSize: '11px', textAlign: 'left' }}>
                      {row.label}
                    </td>
                  </tr>
                );
              }

              const isGet = row.entry_type === 'get';
              return (
                <tr key={idx}>
                  <td style={{ border: '1px solid #cbd5e1', padding: '10px', fontSize: '11px', color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{formatDate(row.entry_date)}</td>
                  <td style={{ border: '1px solid #cbd5e1', padding: '10px', fontSize: '11px', color: '#DC2626', fontWeight: 'bold', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {!isGet ? `₹${parseFloat(row.amount).toLocaleString()}` : ''}
                  </td>
                  <td style={{ border: '1px solid #cbd5e1', padding: '10px', fontSize: '11px', color: '#16A34A', fontWeight: 'bold', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {isGet ? `₹${parseFloat(row.amount).toLocaleString()}` : ''}
                  </td>
                  <td style={{ border: '1px solid #cbd5e1', padding: '10px', fontSize: '11px', fontWeight: 'bold', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    ₹{Math.abs(row.calculated_bal).toLocaleString()}
                    {row.calculated_bal >= 0 ? 'Cr' : 'Dr'}
                  </td>
                  <td style={{ border: '1px solid #cbd5e1', padding: '10px', fontSize: '11px', color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <span style={{ fontWeight: 'bold', fontSize: '12px' }}>
                      {row.party_name || (isGet ? t(LangKeys.received) : t(LangKeys.paid))}
                    </span>
                  </td>
                  <td style={{ border: '1px solid #cbd5e1', padding: '10px', fontSize: '11px', color: '#334155', wordBreak: 'break-word' }}>
                    {row.note || '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div style={{ marginTop: '40px', borderTop: '1px solid #e2e8f0', paddingTop: '15px', textAlign: 'center', fontSize: '10px', color: '#94a3b8', fontWeight: '600' }}>
          {t(LangKeys.generated_by)} | {new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true })}
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    background: '#F1F5F9',
    borderRadius: '24px',
    overflow: 'hidden'
  },
  header: {
    padding: '16px 24px',
    background: '#fff',
    borderBottom: '1px solid #F1F5F9',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
    zIndex: 30
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '16px' },
  iconBtn: {
    width: '40px',
    height: '40px',
    borderRadius: '12px',
    background: '#F1F5F9',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer'
  },
  headerTitle: {
    fontSize: '18px',
    fontWeight: '800',
    color: '#0F172A',
    margin: 0
  },
  headerSub: {
    fontSize: '13px',
    color: '#64748B',
    fontWeight: '600'
  },
  headerRight: { display: 'flex', gap: '12px' },
  downloadBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    borderRadius: '10px',
    background: '#0F172A',
    color: '#fff',
    border: 'none',
    fontWeight: '700',
    cursor: 'pointer'
  },
  scrollArea: {
    flex: 1,
    overflowY: 'auto',
    padding: '40px 20px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  reportSheet: {
    background: '#FFFFFF',
    padding: '40px',
    width: '100%',
    maxWidth: '1000px',
    display: 'flex',
    flexDirection: 'column',
    gap: '30px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
    borderRadius: '4px'
  },
  topHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    width: '100%'
  },
  sideLogo: {
    width: '45px',
    height: '45px',
    objectFit: 'contain'
  },
  bizCenter: {
    textAlign: 'center',
    flex: 1
  },
  bizName: {
    fontSize: '18px',
    fontWeight: '800',
    color: '#1E293B',
    margin: '0 0 4px 0'
  },
  subText: {
    fontSize: '11px',
    color: '#64748B',
    display: 'block',
    fontWeight: '600'
  },
  statementBanner: {
    background: '#1E40AF',
    color: '#FFFFFF',
    padding: '10px',
    textAlign: 'center',
    fontWeight: 'bold',
    fontSize: '16px',
    textTransform: 'uppercase'
  },
  customerInfo: { textAlign: 'center' },
  custName: {
    fontSize: '15px',
    fontWeight: '800',
    color: '#1E293B',
    margin: '0 0 4px 0'
  },
  custMobile: {
    fontSize: '12px',
    color: '#64748B',
    fontWeight: '600'
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '10px'
  },
  sumCard: {
    background: '#F8FAFC',
    padding: '12px 6px',
    borderRadius: '8px',
    border: '1px solid #E2E8F0',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    justifyContent: 'center'
  },
  sumLabel: {
    fontSize: '9px',
    fontWeight: '700',
    color: '#94A3B8',
    textTransform: 'uppercase'
  },
  sumVal: {
    fontSize: '13px',
    fontWeight: '800'
  },
  tableWrapper: { width: '100%' },
  table: { width: '100%', borderCollapse: 'collapse' },
  theadRow: { borderBottom: '2px solid #E2E8F0' },
  th: {
    padding: '10px 8px',
    textAlign: 'left',
    fontSize: '10px',
    color: '#475569',
    fontWeight: '800',
    background: '#F8FAFC'
  },
  monthRow: {
    background: '#E0E7FF',
    color: '#312E81',
    padding: '8px 12px',
    fontWeight: '800',
    fontSize: '11px',
    textAlign: 'left'
  },
  tr: { borderBottom: '1px solid #F1F5F9' },
  td: {
    padding: '10px 8px',
    fontSize: '11px',
    color: '#1E293B'
  },
  footerPrint: {
    marginTop: '40px',
    textAlign: 'center',
    fontSize: '10px',
    color: '#94A3B8',
    fontWeight: '600',
    borderTop: '1px solid #F1F5F9',
    paddingTop: '20px'
  }
};

export default CustomerReportView;